import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { BookOpen, Loader2, User, KeyRound, Hash, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StudentLoginPage = () => {
  const [firstName, setFirstName] = useState('');
  const [pin, setPin] = useState('');
  const [teacherCode, setTeacherCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (pin.length !== 4 || !/^\d{4}$/.test(pin)) {
      setError('PIN must be exactly 4 digits');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API}/student/login`, {
        first_name: firstName,
        pin: pin,
        teacher_code: teacherCode
      });
      
      const { access_token, student } = response.data;
      localStorage.setItem('student_token', access_token);
      localStorage.setItem('student_data', JSON.stringify(student));
      
      toast.success(`Welcome, ${student.first_name}!`);
      navigate('/student/dashboard');
    } catch (err) {
      const message = err.response?.data?.detail || 'Invalid login. Please check your info.';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left Side - Image */}
      <div className="hidden lg:flex flex-1 bg-secondary relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1503676260728-1c00da094a0b?crop=entropy&cs=srgb&fm=jpg&q=85')",
          }}
        />
        <div className="absolute inset-0 bg-primary/50" />
        <div className="relative z-10 flex items-end p-12">
          <div className="text-white max-w-md">
            <h2 className="font-serif text-3xl font-medium mb-4">
              Ready to Learn?
            </h2>
            <p className="text-white/80 text-lg leading-relaxed">
              Enter your name and PIN to start taking your assigned quizzes.
            </p>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <Link to="/" className="flex items-center gap-2 mb-8 group">
            <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center group-hover:scale-105 transition-transform">
              <BookOpen className="w-5 h-5 text-primary-foreground" />
            </div>
            <span className="font-serif text-xl font-semibold text-primary">HomeClass</span>
          </Link>

          <Card className="border-border shadow-lg">
            <CardHeader className="space-y-1 pb-6">
              <CardTitle className="font-serif text-2xl">Student Login</CardTitle>
              <CardDescription className="text-base">
                Enter your name and PIN to access your quizzes
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-5">
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-lg text-sm"
                  >
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {error}
                  </motion.div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="teacherCode" className="text-sm font-medium">Teacher Code</Label>
                  <div className="relative">
                    <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      id="teacherCode"
                      type="text"
                      placeholder="ABC123"
                      value={teacherCode}
                      onChange={(e) => setTeacherCode(e.target.value.toUpperCase())}
                      className="pl-10 h-12 uppercase"
                      maxLength={6}
                      required
                      data-testid="student-teacher-code"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">Ask your teacher for the code</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="firstName" className="text-sm font-medium">First Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      id="firstName"
                      type="text"
                      placeholder="Your first name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      className="pl-10 h-12"
                      required
                      data-testid="student-first-name"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="pin" className="text-sm font-medium">4-Digit PIN</Label>
                  <div className="relative">
                    <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      id="pin"
                      type="password"
                      placeholder="••••"
                      value={pin}
                      onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                      className="pl-10 h-12 tracking-widest"
                      maxLength={4}
                      required
                      data-testid="student-pin"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 text-base bg-primary hover:bg-primary/90"
                  disabled={loading}
                  data-testid="student-login-submit"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    'Start Learning'
                  )}
                </Button>
              </form>

              <div className="mt-4 text-center">
                <Link
                  to="/forgot-password"
                  className="text-sm text-muted-foreground hover:text-primary hover:underline"
                  data-testid="student-forgot-password-link"
                >
                  Forgot your PIN?
                </Link>
              </div>

              <div className="mt-4 text-center">
                <p className="text-sm text-muted-foreground">
                  Are you a teacher?{' '}
                  <Link
                    to="/login"
                    className="text-primary font-medium hover:underline"
                    data-testid="teacher-login-link"
                  >
                    Teacher Login
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default StudentLoginPage;
